"use client";

import { Suspense, useState } from "react";
import { signIn } from "@/lib/auth-client";
import posthog from "posthog-js";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { SignInErrorAlert } from "./sign-in-error-alert";

export default function LoginPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);

  const handleMicrosoftSignIn = async () => {
    setLoading("microsoft");
    setError(null);
    posthog.capture("microsoft_signin_clicked");
    try {
      await signIn.social({
        provider: "microsoft",
        callbackURL: "/",
        // Without this, failures land on /api/auth/error, which bounces
        // through / back to a blank login page with no explanation.
        errorCallbackURL: "/login",
      });
    } catch (err) {
      setError("Sign in failed. Please try again.");
      toast.error("Sign in failed", {
        description: "Please try again.",
      });
      posthog.captureException(err);
      setLoading(null);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading("magic-link");
    setError(null);
    try {
      const { error: err } = await signIn.magicLink({
        email: email.trim(),
        callbackURL: "/",
        errorCallbackURL: "/login",
      });
      if (err) {
        const description = err.message ?? "Failed to send magic link.";
        setError(description);
        toast.error("Failed to send magic link", { description });
        setLoading(null);
      } else {
        posthog.capture("magic_link_requested");
        posthog.identify(email.trim());
        setMagicLinkSent(true);
        setLoading(null);
      }
    } catch (err) {
      setError("Failed to send magic link. Please try again.");
      toast.error("Failed to send magic link", {
        description: "Please try again.",
      });
      posthog.captureException(err);
      setLoading(null);
    }
  };

  if (magicLinkSent) {
    return (
      <div className="border-border bg-card rounded-2xl border p-6 shadow-sm">
        <div className="bg-primary/10 mx-auto flex size-11 items-center justify-center rounded-full">
          <CheckCircle2 className="text-primary size-5" />
        </div>
        <div className="mt-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold tracking-tight">
            Check your email
          </h2>
          <p className="text-muted-foreground text-sm">
            We sent a sign-in link to
          </p>
          <p className="text-sm font-medium">{email}</p>
        </div>
        <p className="text-muted-foreground mt-4 text-center text-xs">
          The link expires in 5 minutes. Check your spam folder if you
          don&apos;t see it.
        </p>
        <Button
          variant="ghost"
          onClick={() => {
            setMagicLinkSent(false);
            setEmail("");
          }}
          className="mt-4 w-full"
        >
          <ArrowLeft />
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <div className="border-border bg-card rounded-2xl border p-6 shadow-sm">
      <div className="mb-5 text-center">
        <h2 className="text-lg font-semibold tracking-tight">Welcome back</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Sign in with your Microsoft work account.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        // useSearchParams needs a Suspense boundary to keep this page static.
        <Suspense fallback={null}>
          <SignInErrorAlert />
        </Suspense>
      )}

      {/* Microsoft SSO — primary sign-in path */}
      <Button
        onClick={handleMicrosoftSignIn}
        disabled={!!loading}
        autoFocus
        className="w-full"
      >
        {loading === "microsoft" ? (
          <Spinner />
        ) : (
          <>
            <svg viewBox="0 0 21 21" className="h-4 w-4" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Continue with Microsoft
          </>
        )}
      </Button>

      <div className="my-5 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted-foreground text-[11px] tracking-widest uppercase">
          or use email
        </span>
        <Separator className="flex-1" />
      </div>

      {/* Magic Link — fallback for users without Microsoft access. Hidden
       * behind a "Sign in with email" affordance so the Microsoft path is
       * the obvious primary action; clicking the button expands the form
       * inline. */}
      {emailFormOpen ? (
        <form onSubmit={handleMagicLink} className="space-y-3">
          <div className="space-y-1.5">
            <Label
              htmlFor="email"
              className="text-muted-foreground text-xs font-medium"
            >
              Work email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@johnstonsgrain.com"
              autoComplete="email"
              autoFocus
              required
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            disabled={!!loading || !email.trim()}
            className="w-full"
          >
            {loading === "magic-link" ? (
              <Spinner />
            ) : (
              <>
                <Mail />
                Send magic link
                <ArrowRight />
              </>
            )}
          </Button>
        </form>
      ) : (
        <div className="text-center">
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={() => {
              setError(null);
              setEmailFormOpen(true);
            }}
            disabled={!!loading}
            className="text-muted-foreground hover:text-foreground gap-1.5"
          >
            <Mail className="size-3" />
            Sign in with email instead
          </Button>
        </div>
      )}

      <p className="text-muted-foreground mt-6 text-center text-[11px]">
        No password needed. Microsoft SSO is fastest — magic link is a one-time
        email fallback.
      </p>
    </div>
  );
}
